import { AgGridReact, DropdownButton, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { TABLE_GRID_OPTIONS } from "../../constants";
import * as styles from "./styles_vanilla.css";
import type { TaxResidencyEntry } from "../../types";

export type GridContext = {
  getCountryName: (code: string) => string;
  onEdit: (entry: TaxResidencyEntry) => void;
  onDelete: (entryId: string) => void;
  readonly: boolean;
};

const NaText = () => (
  <Text className={styles.naText} variant="secondary-md">
    N/A
  </Text>
);

const ActionsCellRenderer = (
  params: AgGridReact.CustomCellRendererProps<TaxResidencyEntry, unknown, GridContext>,
) => {
  const { data, context } = params;
  if (!data || !context) return null;

  return (
    <DropdownButton
      data-testid={`tax-residency-${data.id}-actions`}
      variant="muted"
      icon="ellipsis-h"
      direction="down"
      align="right"
      usePortal
    >
      <DropdownButton.Action disabled={context.readonly} onSelect={() => context.onEdit(data)}>
        <Flex alignItems="center" gap={8}>
          <Icon type="edit" />
          <Text>Edit</Text>
        </Flex>
      </DropdownButton.Action>
      <DropdownButton.Action
        disabled={context.readonly}
        onSelect={() => context.onDelete(data.id)}
      >
        <Flex alignItems="center" gap={8}>
          <Icon type="trash" />
          <Text>Delete</Text>
        </Flex>
      </DropdownButton.Action>
    </DropdownButton>
  );
};

export const columnDefs: AgGridReact.AgGridReactProps<TaxResidencyEntry>["columnDefs"] = [
  {
    field: "jurisdiction",
    headerName: "Jurisdiction of tax residency",
    flex: 1,
    sortable: false,
    cellClass: `${styles.gridCell} ${styles.tableCellWithBorder}`,
    headerClass: `${styles.gridHeader} ${styles.tableCellWithBorder}`,
    cellRenderer: (
      params: AgGridReact.CustomCellRendererProps<TaxResidencyEntry, string, GridContext>,
    ) => {
      const countryName = params.context?.getCountryName(params.value ?? "") ?? params.value;
      return <Text className={styles.cellContent}>{countryName}</Text>;
    },
  },
  {
    field: "taxReferenceNumberType",
    headerName: "Tax reference number type",
    flex: 1,
    sortable: false,
    cellClass: styles.gridCell,
    headerClass: styles.gridHeader,
    cellRenderer: (params: AgGridReact.CustomCellRendererProps<TaxResidencyEntry, string>) =>
      params.value ? <Text className={styles.cellContent}>{params.value}</Text> : <NaText />,
  },
  {
    field: "taxReferenceNumber",
    headerName: "Tax reference number",
    flex: 1,
    sortable: false,
    cellClass: styles.gridCell,
    headerClass: styles.gridHeader,
    cellRenderer: (params: AgGridReact.CustomCellRendererProps<TaxResidencyEntry, string>) =>
      params.value ? <Text className={styles.cellContent}>{params.value}</Text> : <NaText />,
  },
  {
    field: "reasonForNoTin",
    headerName: "No TIN?",
    flex: 1,
    sortable: false,
    cellClass: `${styles.gridCell} ${styles.centeredCell}`,
    headerClass: `${styles.gridHeader} ${styles.centeredCell}`,
    cellRenderer: (params: AgGridReact.CustomCellRendererProps<TaxResidencyEntry, string>) =>
      params.data?.hasTaxReferenceNumber ? (
        <NaText />
      ) : (
        <Text className={styles.cellContent}>{params.value || "Reason for no TIN"}</Text>
      ),
  },
  {
    colId: "actions",
    headerName: "",
    width: 56,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    cellClass: styles.actionsGridCell,
    cellRenderer: ActionsCellRenderer,
  },
];

export const gridOptions = TABLE_GRID_OPTIONS;
